import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import _ from 'lodash';
import { nanoid } from 'nanoid';

import { FeeCreditSourceLabel } from 'constants/loanFee';

import {
  closeDialogAction,
  openDialogAction,
  updateDialogOptionsAction,
} from 'store/global/actions';

import ApplyCreditModal from '../../ApplyCreditModal';

import { creditFormSchema } from './constants';

export const useOpenApplyCreditModal = ({ formContext, onApplyCredit }) => {
  const dispatch = useDispatch();

  const {
    form,
    validationErrors,
    handleChange,
    resetForm,
    isFormChanged,
    setInForm,
    setIsTouched,
    setSchema,
    setValidationErrors,
    validateForm,
  } = formContext;

  const modalId = useMemo(() => nanoid(), []);

  useEffect(() => {
    setSchema(creditFormSchema);
  }, [setSchema]);

  const isFormError = useMemo(
    () => Object.values(validationErrors).some(field => !!field),
    [validationErrors],
  );

  const onCreditSourceChange = useCallback(
    (_e, selectedOption) => {
      setIsTouched(true);
      setInForm('creditSource', selectedOption);
    },
    [setIsTouched, setInForm],
  );

  const onCloseModal = useCallback(() => {
    resetForm();
    setValidationErrors({});

    dispatch(closeDialogAction(modalId));
  }, [dispatch, modalId, resetForm, setValidationErrors]);

  const onSubmit = useCallback(async () => {
    const isValid = await validateForm({ noFormChangedValidate: true });

    if (!isValid) {
      return;
    }

    const creditSource = _.isObject(form.creditSource)
      ? form.creditSource.value
      : form.creditSource;

    const creditSourceName = _.isObject(form.creditSource)
      ? form.creditSource.name
      : FeeCreditSourceLabel[form.creditSource];

    onApplyCredit({
      amount: Number(form.amount),
      creditSource,
      creditSourceName,
    });

    resetForm();
    setValidationErrors({});

    dispatch(closeDialogAction(modalId));
  }, [form, validateForm, onApplyCredit, resetForm, setValidationErrors, dispatch, modalId]);

  const openApplyCreditModal = useCallback(() => {
    dispatch(
      openDialogAction({
        id: modalId,
        header: 'Apply Fee Credit',
        isRestyled: true,
        size: ModalSize.SMALL,
        withButtons: true,
        onAcceptButtonName: 'Apply Credit',
        withClosingConfirmation: true,
        isAcceptButtonDisabled: true,
        afterCloseCallback: onCloseModal,
        onAcceptClick: onSubmit,
        body: () => (
          <ApplyCreditModal
            form={form}
            validationErrors={validationErrors}
            onChange={handleChange}
            onCreditSourceChange={onCreditSourceChange}
          />
        ),
      }),
    );
  }, [
    dispatch,
    modalId,
    onCloseModal,
    onSubmit,
    form,
    validationErrors,
    handleChange,
    onCreditSourceChange,
  ]);

  useEffect(() => {
    dispatch(
      updateDialogOptionsAction(modalId, {
        onAcceptClick: onSubmit,
        afterCloseCallback: onCloseModal,
        isAcceptButtonDisabled: !isFormChanged || isFormError,
        body: () => (
          <ApplyCreditModal
            form={form}
            validationErrors={validationErrors}
            onChange={handleChange}
            onCreditSourceChange={onCreditSourceChange}
          />
        ),
      }),
    );
  }, [
    dispatch,
    modalId,
    onCloseModal,
    onSubmit,
    form,
    validationErrors,
    handleChange,
    onCreditSourceChange,
    isFormChanged,
    isFormError,
  ]);

  return openApplyCreditModal;
};
